import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SpiralCanvas from "./SpiralCanvas";
import AwakeningOverlay from "./AwakeningOverlay";
import GoldenHint from "./GoldenHint";
import BoundaryCanvas from "./BoundaryCanvas";
import GlitchCollapseOverlay from "./GlitchCollapseOverlay";
import CurseRejectionOverlay from "./CurseRejectionOverlay";
import { useInviteCode } from "./useInviteCode";

const COUNTDOWN_START = 60;
const WRONG_PENALTY = 10;

function checkFromAbyss(): boolean {
  return sessionStorage.getItem("from_abyss") === "true";
}

function useGlitch(text: string, interval = 8000) {
  const [display, setDisplay] = useState(text);
  const chars = "アイウエオカキクケコ#@!%&*?†‡§¶";

  useEffect(() => {
    const schedule = () =>
      setTimeout(() => {
        let frame = 0;
        const id = setInterval(() => {
          frame++;
          if (frame > 14) {
            setDisplay(text);
            clearInterval(id);
            timerRef.current = schedule();
            return;
          }
          setDisplay(
            text
              .split("")
              .map((c) =>
                Math.random() < 0.35
                  ? chars[Math.floor(Math.random() * chars.length)]
                  : c
              )
              .join("")
          );
        }, 70);
      }, interval + Math.random() * 4000);

    const timerRef = { current: schedule() };
    return () => clearTimeout(timerRef.current);
  }, [text, interval]);

  return display;
}

// ── ロック状態（ローカル、累計ミス対策） ──
const STORAGE_KEY_TOTAL_MISS = "vault_total_miss";
const STORAGE_KEY_LOCK_UNTIL = "vault_lock_until";
const TOTAL_MISS_LOCK_THRESHOLD = 10;
const LOCK_DURATION_MS = 30 * 60 * 1000;

function getTotalMiss(): number {
  return parseInt(localStorage.getItem(STORAGE_KEY_TOTAL_MISS) ?? "0", 10);
}
function incrementTotalMiss(): number {
  const next = getTotalMiss() + 1;
  localStorage.setItem(STORAGE_KEY_TOTAL_MISS, String(next));
  return next;
}
function getLockUntil(): number {
  return parseInt(localStorage.getItem(STORAGE_KEY_LOCK_UNTIL) ?? "0", 10);
}
function setLock(): void {
  localStorage.setItem(STORAGE_KEY_LOCK_UNTIL, String(Date.now() + LOCK_DURATION_MS));
}
function clearLockStorage(): void {
  localStorage.removeItem(STORAGE_KEY_LOCK_UNTIL);
  localStorage.removeItem(STORAGE_KEY_TOTAL_MISS);
}

interface LockInfo {
  isLocked: boolean;
  lockRemainingSeconds: number;
}

function checkLockStatus(): LockInfo {
  const lockUntil = getLockUntil();
  const now = Date.now();
  if (lockUntil > now) {
    return { isLocked: true, lockRemainingSeconds: Math.ceil((lockUntil - now) / 1000) };
  }
  if (lockUntil > 0 && lockUntil <= now) clearLockStorage();
  return { isLocked: false, lockRemainingSeconds: 0 };
}

export default function VaultPage() {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<"idle" | "wrong" | "spiral" | "done" | "expired" | "verifying">("idle");
  const [wrongReason, setWrongReason] = useState<string>("");
  const [mounted, setMounted] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { verifyCode } = useInviteCode();

  const [lockInfo, setLockInfo] = useState<LockInfo>(() => checkLockStatus());
  const [rateLimited, setRateLimited] = useState(false);

  const [timeLeft, setTimeLeft] = useState(COUNTDOWN_START);
  const [countdownStarted, setCountdownStarted] = useState(false);
  const [penaltyFlash, setPenaltyFlash] = useState(false);
  const [shakeLevel, setShakeLevel] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [mistCount, setMistCount] = useState(0);
  const [redPulseActive, setRedPulseActive] = useState(false);
  const redPulseRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [glitchActive, setGlitchActive] = useState(false);
  const [curseActive, setCurseActive] = useState(false);
  const curseTriggeredRef = useRef(false);

  // ロック残り時間カウントダウン
  useEffect(() => {
    if (!lockInfo.isLocked) return;
    const id = setInterval(() => {
      const info = checkLockStatus();
      setLockInfo(info);
      if (!info.isLocked) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lockInfo.isLocked]);

  const fromAbyss = checkFromAbyss();

  const [showAwakening, setShowAwakening] = useState(() => {
    return sessionStorage.getItem("vault_unlocked") === "true";
  });
  const [awakeningDone, setAwakeningDone] = useState(false);
  const fromRevelation =
    showAwakening ||
    sessionStorage.getItem("vault_from_revelation") === "true";

  const abyssTitle = useGlitch("深淵の扉", 6000);
  const abyssSub = useGlitch("お前はすでに選ばれている", 9000);

  useEffect(() => {
    const delay = showAwakening ? 2400 : 100;
    const timer = setTimeout(() => {
      setMounted(true);
      setTimeout(() => setCountdownStarted(true), 1200);
    }, delay);
    return () => clearTimeout(timer);
  }, [showAwakening]);

  useEffect(() => {
    if (!countdownStarted || status === "spiral" || status === "done" || status === "expired") return;
    countdownRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 10) setShakeLevel(3);
        else if (next <= 20) setShakeLevel(2);
        else if (next <= 35) setShakeLevel(1);
        else setShakeLevel(0);
        if (next <= 0) {
          clearInterval(countdownRef.current!);
          setStatus("expired");
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [countdownStarted, status]);

  useEffect(() => {
    if (timeLeft <= 10 && timeLeft > 0 && countdownStarted && status !== "spiral" && status !== "done") {
      if (redPulseRef.current) return;
      setRedPulseActive(true);
      let pulseOn = true;
      redPulseRef.current = setInterval(() => {
        pulseOn = !pulseOn;
        setRedPulseActive(pulseOn);
      }, timeLeft <= 5 ? 250 : 450);
    } else {
      if (redPulseRef.current) { clearInterval(redPulseRef.current); redPulseRef.current = null; }
      setRedPulseActive(false);
    }
    return () => { if (redPulseRef.current) { clearInterval(redPulseRef.current); redPulseRef.current = null; } };
  }, [timeLeft, countdownStarted, status]);

  useEffect(() => {
    if (status === "expired") {
      setGlitchActive(true);
      if (redPulseRef.current) { clearInterval(redPulseRef.current); redPulseRef.current = null; }
      setRedPulseActive(false);
      setTimeout(() => {
        setGlitchActive(false);
        setTimeLeft(COUNTDOWN_START);
        setShakeLevel(0);
        setCode("");
        setMistCount(0);
        setStatus("idle");
        setCountdownStarted(false);
        setTimeout(() => setCountdownStarted(true), 800);
      }, 4000);
    }
  }, [status]);

  const handleCurseComplete = useCallback(() => {
    setCurseActive(false);
    curseTriggeredRef.current = false;
    setMistCount(0);
    setTimeLeft(COUNTDOWN_START);
    setShakeLevel(0);
    setCode("");
    setStatus("idle");
    setCountdownStarted(false);
    setTimeout(() => setCountdownStarted(true), 600);
  }, []);

  const handleAwakeningComplete = useCallback(() => {
    setAwakeningDone(true);
    setShowAwakening(false);
    sessionStorage.setItem("vault_from_revelation", "true");
    sessionStorage.removeItem("vault_unlocked");
  }, []);

  const handleSpiralComplete = useCallback(() => {
    setStatus("done");
    if (countdownRef.current) clearInterval(countdownRef.current);
    sessionStorage.removeItem("from_abyss");
    sessionStorage.setItem("vault_unlocked", "true");
    sessionStorage.setItem("vault_just_arrived", "true");
    setTimeout(() => navigate("/"), 200);
  }, [navigate]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const currentLock = checkLockStatus();
    if (currentLock.isLocked) { setLockInfo(currentLock); return; }
    if (status === "verifying" || status === "spiral" || status === "done") return;

    setStatus("verifying");
    setRateLimited(false);
    setWrongReason("");

    const result = await verifyCode(code);

    if (result.reason === "rate_limited") {
      setRateLimited(true);
      setCode("");
      setStatus("idle");
      setTimeout(() => setRateLimited(false), 4000);
      return;
    }

    if (result.success) {
      clearLockStorage();
      if (countdownRef.current) clearInterval(countdownRef.current);
      // name・tier を保存（ホームで表示用）
      if (result.name) sessionStorage.setItem("vault_member_name", result.name);
      if (result.tier) sessionStorage.setItem("vault_member_tier", result.tier);
      setStatus("spiral");
    } else {
      setStatus("wrong");
      setCode("");

      const totalMiss = incrementTotalMiss();
      if (totalMiss >= TOTAL_MISS_LOCK_THRESHOLD) {
        setLock();
        setLockInfo(checkLockStatus());
        if (countdownRef.current) clearInterval(countdownRef.current);
        setShakeLevel(0);
        setTimeout(() => setStatus("idle"), 2200);
        return;
      }

      const reasonMsg = result.reason === "limit_reached"
        ? "このコードはすでに使用上限に達している。"
        : result.reason === "expired"
        ? "このコードの有効期限が切れている。"
        : result.reason === "inactive"
        ? "このコードは無効化されている。"
        : fromAbyss
        ? "お前はまだ、深淵の言葉を知らない。"
        : "この刻印は、あなたのものではない。";
      setWrongReason(reasonMsg);

      setMistCount((c) => {
        const next = c + 1;
        if (next >= 3 && !curseTriggeredRef.current) {
          curseTriggeredRef.current = true;
          setTimeout(() => {
            if (countdownRef.current) clearInterval(countdownRef.current);
            setShakeLevel(0);
            setCurseActive(true);
          }, 800);
        }
        return next;
      });

      setTimeLeft((prev) => {
        const next = Math.max(1, prev - WRONG_PENALTY);
        if (next <= 10) setShakeLevel(3);
        else if (next <= 20) setShakeLevel(2);
        else if (next <= 35) setShakeLevel(1);
        return next;
      });
      setPenaltyFlash(true);
      setTimeout(() => setPenaltyFlash(false), 800);
      setTimeout(() => setStatus("idle"), 2200);
    }
  }, [code, status, verifyCode, fromAbyss]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit(e as unknown as React.FormEvent);
  };

  const isEntering = status === "spiral" || status === "done";
  const isExpired = status === "expired";
  const isVerifying = status === "verifying";

  const pulseIntensity = timeLeft <= 5 ? 0.28 : timeLeft <= 10 ? 0.18 : 0;

  const gold = "rgba(201,168,76,";
  const crimson = "rgba(180,30,30,";
  const accent = fromAbyss ? crimson : gold;

  const timerColor = timeLeft <= 10
    ? "rgba(255,60,60,0.95)"
    : timeLeft <= 20
    ? "rgba(255,120,40,0.85)"
    : timeLeft <= 35
    ? "rgba(220,160,40,0.75)"
    : "rgba(201,168,76,0.5)";

  const timerGlow = timeLeft <= 10
    ? "0 0 20px rgba(255,0,0,0.6), 0 0 40px rgba(200,0,0,0.3)"
    : timeLeft <= 20
    ? "0 0 14px rgba(255,100,0,0.4)"
    : "none";

  const shakeAnim = shakeLevel === 3
    ? "hellShake3"
    : shakeLevel === 2
    ? "hellShake2"
    : shakeLevel === 1
    ? "hellShake1"
    : "none";

  return (
    <>
      {showAwakening && !awakeningDone && (
        <AwakeningOverlay onComplete={handleAwakeningComplete} />
      )}

      <SpiralCanvas
        active={isEntering}
        onComplete={handleSpiralComplete}
        loopCount={fromAbyss ? 6 : 1}
      />

      <BoundaryCanvas fromAbyss={fromAbyss} timeLeft={timeLeft} countdownMax={COUNTDOWN_START} />

      <GlitchCollapseOverlay active={glitchActive} fromAbyss={fromAbyss} />
      <CurseRejectionOverlay active={curseActive} onComplete={handleCurseComplete} />

      {redPulseActive && !isExpired && (
        <div
          className="fixed inset-0 pointer-events-none z-40"
          style={{
            background: `radial-gradient(ellipse at 50% 50%, rgba(200,0,0,${pulseIntensity}) 0%, rgba(150,0,0,${pulseIntensity * 0.5}) 40%, transparent 70%)`,
          }}
        />
      )}

      {penaltyFlash && (
        <div
          className="fixed inset-0 pointer-events-none z-50"
          style={{ background: "rgba(200,0,0,0.18)", animation: "penaltyFlash 0.8s ease forwards" }}
        />
      )}

      <div
        className="relative min-h-screen w-full flex flex-col overflow-hidden"
        style={{
          opacity: isEntering ? 0 : 1,
          transition: isEntering ? "opacity 0.6s ease" : "none",
          pointerEvents: isEntering ? "none" : "auto",
          animation: shakeLevel > 0 && !isExpired
            ? `${shakeAnim} ${shakeLevel === 3 ? "0.12s" : shakeLevel === 2 ? "0.18s" : "0.3s"} ease-in-out infinite`
            : "none",
          filter: isExpired ? "brightness(0.3) saturate(0)" : "none",
        }}
      >
        {/* カウントダウンタイマー */}
        {countdownStarted && !isEntering && (
          <div className="fixed top-0 left-0 right-0 z-40 flex flex-col items-center pt-4 md:pt-6" style={{ pointerEvents: "none" }}>
            <div className="relative flex flex-col items-center" style={{ animation: timeLeft <= 10 ? "timerPanic 0.15s ease-in-out infinite alternate" : "none" }}>
              <div
                className="font-light tabular-nums"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: timeLeft <= 10 ? "clamp(2.5rem, 7vw, 5rem)" : "clamp(1.8rem, 4vw, 3rem)",
                  color: timerColor,
                  textShadow: timerGlow,
                  letterSpacing: "0.05em",
                  lineHeight: 1,
                  transition: "font-size 0.3s ease, color 0.5s ease",
                }}
              >
                {String(timeLeft).padStart(2, "0")}
              </div>
              <div
                className="text-xs tracking-[0.3em] md:tracking-[0.5em] uppercase mt-1"
                style={{
                  color: timeLeft <= 10 ? "rgba(255,60,60,0.5)" : "rgba(201,168,76,0.2)",
                  fontFamily: "'Cormorant Garamond', serif",
                  transition: "color 0.5s ease",
                }}
              >
                {timeLeft <= 10 ? "FINAL WARNING" : timeLeft <= 20 ? "HURRY" : "SECONDS REMAIN"}
              </div>
            </div>
            <div className="mt-3 w-36 md:w-48 h-px relative overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
              <div
                className="absolute left-0 top-0 h-full"
                style={{
                  width: `${(timeLeft / COUNTDOWN_START) * 100}%`,
                  background: timeLeft <= 10
                    ? "linear-gradient(to right, rgba(200,0,0,0.8), rgba(255,60,60,0.9))"
                    : timeLeft <= 20
                    ? "linear-gradient(to right, rgba(200,80,0,0.6), rgba(255,140,40,0.8))"
                    : "linear-gradient(to right, rgba(201,168,76,0.3), rgba(255,220,100,0.5))",
                  boxShadow: timeLeft <= 10 ? "0 0 8px rgba(255,0,0,0.6)" : "none",
                  transition: "width 0.9s linear, background 0.5s ease",
                }}
              />
            </div>
            {penaltyFlash && (
              <div className="mt-2 text-xs tracking-[0.3em]" style={{ color: "rgba(255,60,60,0.8)", fontFamily: "'Cormorant Garamond', serif", animation: "penaltyText 0.8s ease forwards" }}>
                −{WRONG_PENALTY}秒
              </div>
            )}
          </div>
        )}

        {/* 天界エリア */}
        <div className="relative flex-1 flex flex-col items-center justify-end pb-10 md:pb-16 z-10">
          <div
            className="flex flex-col items-center mb-6 md:mb-10"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(-20px)", transition: "opacity 2s ease 0.5s, transform 2s ease 0.5s" }}
          >
            <div className="relative flex items-center justify-center mb-4 md:mb-6" style={{ width: "60px", height: "60px" }}>
              <div className="absolute inset-0 rounded-full" style={{ border: fromAbyss ? "1px solid rgba(180,30,30,0.2)" : "1px solid rgba(201,168,76,0.2)", animation: "ringRotate 20s linear infinite" }} />
              <div className="absolute rounded-full" style={{ inset: "12px", border: fromAbyss ? "1px solid rgba(180,30,30,0.35)" : "1px solid rgba(201,168,76,0.35)", animation: "ringRotate 12s linear infinite reverse" }} />
              <div className="w-2 h-2 rounded-full" style={{ background: fromAbyss ? "rgba(200,60,60,0.6)" : "rgba(255,220,120,0.7)", boxShadow: fromAbyss ? "0 0 12px rgba(200,60,60,0.4), 0 0 24px rgba(180,30,30,0.2)" : "0 0 12px rgba(255,220,120,0.5), 0 0 24px rgba(201,168,76,0.25)", animation: "corePulse 3s ease-in-out infinite" }} />
              <div className="absolute w-full h-px" style={{ background: fromAbyss ? "linear-gradient(to right, transparent, rgba(180,30,30,0.15), transparent)" : "linear-gradient(to right, transparent, rgba(201,168,76,0.15), transparent)" }} />
              <div className="absolute h-full w-px" style={{ background: fromAbyss ? "linear-gradient(to bottom, transparent, rgba(180,30,30,0.15), transparent)" : "linear-gradient(to bottom, transparent, rgba(201,168,76,0.15), transparent)" }} />
            </div>
            <div className="text-xs tracking-[0.5em] md:tracking-[0.7em] uppercase mb-2" style={{ color: fromAbyss ? `${crimson}0.35)` : `${gold}0.35)`, fontFamily: "'Cormorant Garamond', serif" }}>
              {fromAbyss ? "ABYSS" : "MAVERICK"}
            </div>
            <div className="w-12 h-px" style={{ background: fromAbyss ? "linear-gradient(to right, transparent, rgba(180,30,30,0.25), transparent)" : "linear-gradient(to right, transparent, rgba(201,168,76,0.25), transparent)" }} />
          </div>

          <div className="text-center px-4 md:px-6" style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(-12px)", transition: "opacity 2s ease 0.8s, transform 2s ease 0.8s" }}>
            {fromAbyss ? (
              <h1 className="font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 4vw, 3rem)", fontWeight: 300, color: `${crimson}0.7)`, textShadow: "0 0 40px rgba(150,0,0,0.25)", letterSpacing: "0.06em" }}>
                {abyssTitle}
              </h1>
            ) : (
              <h1 className="font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 4vw, 3rem)", fontWeight: 300, color: "rgba(255,255,255,0.55)", letterSpacing: "0.06em" }}>
                天界と地球の<br />
                <span style={{ color: `${gold}0.45)`, fontSize: "0.72em", letterSpacing: "0.14em" }}>狭間にて、契約する</span>
              </h1>
            )}
          </div>

          <div className="mt-4 md:mt-5 text-center px-4" style={{ opacity: mounted ? 1 : 0, transition: "opacity 2s ease 1.1s" }}>
            {fromAbyss ? (
              <p className="text-xs tracking-[0.3em]" style={{ color: `${crimson}0.4)`, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>{abyssSub}</p>
            ) : (
              <p className="text-xs tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.15)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                神の啓示を受けた者のみが、この境界に立つことを許される
              </p>
            )}
          </div>
        </div>

        {/* 境界面 */}
        <div className="relative z-20 flex flex-col items-center" style={{ opacity: mounted ? 1 : 0, transition: "opacity 2s ease 1.3s" }}>
          <div className="w-full flex items-center justify-center mb-0">
            <div className="flex-1 h-px" style={{ background: fromAbyss ? "linear-gradient(to right, transparent, rgba(180,30,30,0.12))" : "linear-gradient(to right, transparent, rgba(201,168,76,0.12))" }} />
            <div className="flex items-center gap-2 md:gap-3 px-4 md:px-6" style={{ minWidth: "160px", justifyContent: "center" }}>
              <div className="w-1 h-1 rounded-full" style={{ background: fromAbyss ? `${crimson}0.3)` : `${gold}0.3)` }} />
              <div className="text-xs tracking-[0.3em] md:tracking-[0.5em] uppercase" style={{ color: fromAbyss ? `${crimson}0.25)` : `${gold}0.25)`, fontFamily: "'Cormorant Garamond', serif" }}>
                {fromAbyss ? "BOUNDARY" : "COVENANT"}
              </div>
              <div className="w-1 h-1 rounded-full" style={{ background: fromAbyss ? `${crimson}0.3)` : `${gold}0.3)` }} />
            </div>
            <div className="flex-1 h-px" style={{ background: fromAbyss ? "linear-gradient(to left, transparent, rgba(180,30,30,0.12))" : "linear-gradient(to left, transparent, rgba(201,168,76,0.12))" }} />
          </div>

          <div className="relative w-full flex flex-col items-center py-8 md:py-12 px-4 md:px-6" style={{ maxWidth: "480px" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: fromAbyss ? "radial-gradient(ellipse 80% 100% at 50% 50%, rgba(80,5,5,0.25) 0%, transparent 70%)" : "radial-gradient(ellipse 80% 100% at 50% 50%, rgba(30,20,60,0.3) 0%, transparent 70%)" }} />

            {/* ロック中バナー */}
            {lockInfo.isLocked && (
              <div className="w-full mb-6 text-center" style={{ padding: "12px 16px", border: "1px solid rgba(180,30,30,0.2)", background: "rgba(80,5,5,0.3)" }}>
                <p className="text-xs tracking-[0.2em]" style={{ color: "rgba(220,80,80,0.8)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>封印が発動した。</p>
                <p className="text-xs tracking-[0.15em] mt-1" style={{ color: "rgba(180,60,60,0.6)", fontFamily: "'Cormorant Garamond', serif" }}>
                  {Math.floor(lockInfo.lockRemainingSeconds / 60)}分{lockInfo.lockRemainingSeconds % 60}秒後に解除される
                </p>
              </div>
            )}

            {/* レート制限バナー */}
            {rateLimited && (
              <div className="w-full mb-6 text-center" style={{ padding: "12px 16px", border: "1px solid rgba(180,30,30,0.15)", background: "rgba(60,5,5,0.25)" }}>
                <p className="text-xs tracking-[0.2em]" style={{ color: "rgba(200,80,80,0.7)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>試みが速すぎる。少し待て。</p>
              </div>
            )}

            {fromRevelation && (
              <GoldenHint isTyping={code.length > 0} mistCount={mistCount} />
            )}

            {fromAbyss && (
              <p className="text-xs tracking-[0.25em] mb-6 text-center" style={{ color: `${crimson}0.3)`, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                お前は知っているはずだ
              </p>
            )}

            {/* 招待の印（入力欄の上） */}
            <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8 w-full" style={{ maxWidth: "320px" }}>
              <div className="flex-1 h-px" style={{ background: fromAbyss ? `linear-gradient(to right, transparent, ${crimson}0.15))` : `linear-gradient(to right, transparent, ${gold}0.15))` }} />
              <div className="text-xs tracking-[0.25em] md:tracking-[0.4em]" style={{ color: fromAbyss ? `${crimson}0.2)` : `${gold}0.2)`, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", whiteSpace: "nowrap" }}>
                {fromAbyss ? "深淵の刻印" : "招待の刻印"}
              </div>
              <div className="flex-1 h-px" style={{ background: fromAbyss ? `linear-gradient(to left, transparent, ${crimson}0.15))` : `linear-gradient(to left, transparent, ${gold}0.15))` }} />
            </div>

            {/* 招待コード入力 */}
            <form onSubmit={handleSubmit} className="w-full flex flex-col items-center" style={{ maxWidth: "320px" }}>
              <div className="relative w-full mb-8">
                {fromRevelation && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      inset: "-12px -8px",
                      borderRadius: "4px",
                      background: "radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.06) 0%, transparent 70%)",
                      animation: code.length === 0 ? "inputAmbient 4s ease-in-out infinite" : "none",
                      opacity: code.length > 0 ? 0 : 1,
                      transition: "opacity 0.8s ease",
                    }}
                  />
                )}

                <input
                  ref={inputRef}
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  placeholder={fromAbyss ? "† · · · · · · · †" : "· · · · · · · ·"}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  disabled={lockInfo.isLocked || isVerifying}
                  className="w-full bg-transparent text-center outline-none"
                  style={{
                    color: status === "wrong" ? "rgba(180,80,80,0.7)" : fromAbyss ? `${crimson}0.7)` : "rgba(255,255,255,0.6)",
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "1rem",
                    letterSpacing: "0.35em",
                    padding: "16px 0",
                    borderBottom: focused
                      ? `1px solid ${fromRevelation && !fromAbyss ? `rgba(201,168,76,${status === "wrong" ? "0" : "0.55"})` : `${accent}${status === "wrong" ? "0" : fromAbyss ? "0.5" : "0.45"})`}`
                      : `1px solid rgba(255,255,255,${status === "wrong" ? "0" : "0.1"})`,
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    caretColor: fromRevelation && !fromAbyss ? `${gold}0.7)` : fromAbyss ? `${crimson}0.7)` : `${gold}0.6)`,
                    transition: "border-color 0.7s ease, color 0.5s ease",
                  }}
                />

                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px"
                  style={{
                    width: focused ? "100%" : "0%",
                    background: fromRevelation && !fromAbyss
                      ? "linear-gradient(to right, transparent, rgba(201,168,76,0.55), transparent)"
                      : fromAbyss
                      ? `linear-gradient(to right, transparent, ${crimson}0.45), transparent)`
                      : `linear-gradient(to right, transparent, ${gold}0.4), transparent)`,
                    boxShadow: focused
                      ? fromRevelation && !fromAbyss
                        ? "0 0 16px rgba(201,168,76,0.3), 0 0 32px rgba(255,200,60,0.15)"
                        : fromAbyss ? "0 0 12px rgba(180,0,0,0.3)" : "0 0 12px rgba(201,168,76,0.2)"
                      : "none",
                    transition: "width 0.7s ease, box-shadow 0.7s ease",
                  }}
                />

                {status === "wrong" && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-full" style={{ background: "linear-gradient(to right, transparent, rgba(180,80,80,0.5), transparent)", animation: "wrongPulse 0.4s ease 3" }} />
                )}
              </div>

              {/* エラーメッセージ */}
              <div className="mb-6 h-5 flex items-center justify-center" style={{ opacity: status === "wrong" ? 1 : 0, transition: "opacity 0.4s ease" }}>
                <span className="text-xs tracking-[0.3em]" style={{ color: "rgba(180,80,80,0.55)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                  {wrongReason || (fromAbyss ? "お前はまだ、深淵の言葉を知らない。" : "この刻印は、あなたのものではない。")}
                </span>
              </div>

              {/* 契約ボタン */}
              <button
                type="submit"
                disabled={code.length === 0 || lockInfo.isLocked || isVerifying}
                className="relative cursor-pointer whitespace-nowrap"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "0",
                  opacity: (code.length === 0 || lockInfo.isLocked) ? 0.3 : isVerifying ? 0.6 : 1,
                  transition: "opacity 0.5s ease",
                  cursor: (lockInfo.isLocked || isVerifying) ? "not-allowed" : "pointer",
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-px" style={{ background: `${accent}0.3)` }} />
                  <span className="text-xs uppercase" style={{ color: `${accent}0.65)`, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.5em" }}>
                    {isVerifying ? "···" : fromAbyss ? "DESCEND" : "SEAL"}
                  </span>
                  <div className="w-8 h-px" style={{ background: `${accent}0.3)` }} />
                </div>
              </button>
            </form>
          </div>

          <div className="w-full flex items-center justify-center mt-0">
            <div className="flex-1 h-px" style={{ background: fromAbyss ? "linear-gradient(to right, transparent, rgba(180,30,30,0.12))" : "linear-gradient(to right, transparent, rgba(201,168,76,0.12))" }} />
            <div className="px-6">
              <div className="w-1 h-1 rounded-full" style={{ background: fromAbyss ? `${crimson}0.2)` : `${gold}0.2)` }} />
            </div>
            <div className="flex-1 h-px" style={{ background: fromAbyss ? "linear-gradient(to left, transparent, rgba(180,30,30,0.12))" : "linear-gradient(to left, transparent, rgba(201,168,76,0.12))" }} />
          </div>
        </div>

        {/* 地球エリア */}
        <div className="relative flex-1 flex flex-col items-center justify-start pt-10 md:pt-16 z-10">
          <div className="text-center px-4 md:px-6" style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(12px)", transition: "opacity 2s ease 1.5s, transform 2s ease 1.5s" }}>
            {fromAbyss ? (
              <div className="flex flex-col gap-3">
                <p className="text-xs tracking-[0.3em]" style={{ color: `${crimson}0.25)`, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                  螺旋を5度越えた者のみ、ここに立つことができる
                </p>
              </div>
            ) : (
              <p className="text-xs tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.1)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                一致した者にだけ、開かれる。
              </p>
            )}
          </div>

          <div className="mt-8 md:mt-10 flex flex-col items-center gap-3" style={{ opacity: mounted ? 1 : 0, transition: "opacity 2.5s ease 1.8s" }}>
            <div className="flex items-center gap-3">
              <div className="w-16 h-px" style={{ background: fromAbyss ? "linear-gradient(to right, transparent, rgba(180,30,30,0.1))" : "linear-gradient(to right, transparent, rgba(255,255,255,0.06))" }} />
              <div className="w-0.5 h-0.5 rounded-full" style={{ background: fromAbyss ? "rgba(180,30,30,0.15)" : "rgba(255,255,255,0.08)" }} />
              <div className="w-16 h-px" style={{ background: fromAbyss ? "linear-gradient(to left, transparent, rgba(180,30,30,0.1))" : "linear-gradient(to left, transparent, rgba(255,255,255,0.06))" }} />
            </div>
            {fromAbyss ? (
              <p className="text-xs tracking-[0.3em]" style={{ color: `${crimson}0.15)`, fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                深淵は、お前を記憶している
              </p>
            ) : (
              <a
                href="mailto:access@maverick.pink"
                className="text-xs tracking-[0.3em] cursor-pointer"
                style={{ color: "rgba(255,255,255,0.08)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", textDecoration: "none", transition: "color 0.7s ease" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.08)"; }}
              >
                招待を受けた者のみ
              </a>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');
        @keyframes wrongPulse { 0%, 100% { opacity: 0; } 50% { opacity: 1; } }
        @keyframes inputAmbient {
          0%, 100% { background: radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.04) 0%, transparent 70%); }
          50% { background: radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.1) 0%, transparent 70%); }
        }
        @keyframes ringRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes corePulse { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }
        @keyframes hellShake1 { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-1px) translateY(0.5px); } 75% { transform: translateX(1px) translateY(-0.5px); } }
        @keyframes hellShake2 { 0%, 100% { transform: translateX(0) rotate(0deg); } 20% { transform: translateX(-2px) translateY(1px) rotate(-0.2deg); } 40% { transform: translateX(2px) translateY(-1px) rotate(0.2deg); } 60% { transform: translateX(-1.5px) translateY(0.5px) rotate(-0.1deg); } 80% { transform: translateX(1.5px) translateY(-0.5px) rotate(0.1deg); } }
        @keyframes hellShake3 { 0%, 100% { transform: translateX(0) rotate(0deg); } 10% { transform: translateX(-3px) translateY(2px) rotate(-0.4deg); } 20% { transform: translateX(3px) translateY(-2px) rotate(0.4deg); } 30% { transform: translateX(-2px) translateY(1px) rotate(-0.2deg); } 40% { transform: translateX(2px) translateY(-1px) rotate(0.2deg); } 50% { transform: translateX(-3px) translateY(2px) rotate(-0.3deg); } 60% { transform: translateX(3px) translateY(-2px) rotate(0.3deg); } 70% { transform: translateX(-2px) translateY(1px) rotate(-0.2deg); } 80% { transform: translateX(2px) translateY(-1px) rotate(0.2deg); } 90% { transform: translateX(-1px) translateY(0.5px) rotate(-0.1deg); } }
        @keyframes timerPanic { 0% { transform: scale(1) translateX(0); } 100% { transform: scale(1.04) translateX(1px); } }
        @keyframes penaltyFlash { 0% { opacity: 0; } 20% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes penaltyText { 0% { opacity: 0; transform: translateY(0); } 20% { opacity: 1; transform: translateY(-4px); } 80% { opacity: 0.8; transform: translateY(-8px); } 100% { opacity: 0; transform: translateY(-12px); } }
        @keyframes expiredFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes expiredPulse { from { opacity: 0.7; transform: scale(0.98); } to { opacity: 1; transform: scale(1.02); } }
        input::placeholder { color: rgba(255,255,255,0.12); letter-spacing: 0.5em; }
      `}</style>
    </>
  );
}
